import React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPencil, faPlay, faRedo } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import ShopmyImage from '../General/ShopmyImage';
import Tooltip from '../General/Tooltip';
import { getCreatorProfileUrl } from '../../helpers/helpers';
import {
  METHOD_META,
  bandOf,
  getPercentDisplay,
  getPlatformDisplay,
  getCreatorTierDisplay,
  getDisplayProduct,
  getQualityScoreBadge,
  getReviewMeta,
  isRunRow,
  isUnmatchedRow,
  isWatchablePost
} from '../../helpers/product_mention_helpers';
import './ProductMentionsTable.scss';

const ProductMentionsTable = props => {
  const { rows, onOpen, onCheck, onConfirm, onRetry, isChecked, canSelect, showStatus } = props;
  const columns = [
    { key: 'check', label: '' },
    { key: 'mention', label: 'Mention' },
    { key: 'product', label: 'Product' },
    { key: 'brand', label: 'Brand' },
    { key: 'confidence', label: 'Conf' },
    { key: 'method', label: 'Method' },
    ...(showStatus ? [{ key: 'status', label: 'Status' }] : []),
    { key: 'actions', label: '' }
  ];

  return (
    <div className={cn('product-mentions-table', { 'with-status': showStatus })}>
      <div className='pmt-grid header'>
        {columns.map(column => (
          <span key={column.key} className='th'>
            {column.label}
          </span>
        ))}
      </div>
      {rows.map(row => {
        const product = getDisplayProduct(row);
        const method = METHOD_META[row.method] || METHOD_META.text;
        const review = getReviewMeta(row);
        const checked = isChecked(row);
        const run = isRunRow(row);
        const grade = !run && getQualityScoreBadge(row);
        const unmatched = isUnmatchedRow(row);
        const band = bandOf(row.confidence);
        const tierDisplay = getCreatorTierDisplay(row.creatorTier);
        return (
          <div key={row.id} className={cn('pmt-grid row', { checked })} onClick={() => onOpen(row)}>
            {canSelect ? (
              <div
                className={cn('pmt-check', { checked })}
                onClick={e => {
                  e.stopPropagation();
                  onCheck(row);
                }}
              >
                <FontAwesomeIcon icon={faCheck} />
              </div>
            ) : (
              <div />
            )}
            <div className='pmt-mention'>
              <div className='pmt-thumb-wrap'>
                {row.postImage ? <ShopmyImage className='pmt-thumb' source={row.postImage} alt='' loading='lazy' decoding='async' /> : <div className='pmt-thumb placeholder' />}
                {isWatchablePost(row) && <FontAwesomeIcon icon={faPlay} className='pmt-play' />}
              </div>
              <div className='pmt-mention-text'>
                {row.creatorUsername ? (
                  <a
                    className='pmt-handle'
                    href={getCreatorProfileUrl(row.creatorUsername)}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={e => e.stopPropagation()}
                  >
                    @{row.creatorUsername}
                  </a>
                ) : (
                  <div className='pmt-handle'>Post #{row.SocialPost_id}</div>
                )}
                <div className='pmt-sub'>
                  {getPlatformDisplay(row.platform)} · {moment(row.contentPublishedAt || row.createdAt).fromNow(true)}
                  {tierDisplay && ` · ${tierDisplay}`}
                </div>
              </div>
            </div>
            <div className='pmt-product'>
              {product.image ? <ShopmyImage className='pmt-thumb' source={product.image} alt='' loading='lazy' decoding='async' /> : <div className='pmt-thumb placeholder' />}
              <div className='pmt-product-text'>
                <div className={cn('pmt-product-title', { overridden: product.isOverridden })}>{product.title}</div>
                <div className='pmt-sub'>
                  {product.productId ? `#${product.productId}${row.matchCount > 1 ? ` · +${row.matchCount - 1}` : ''}` : '—'}
                </div>
              </div>
            </div>
            <div className='pmt-brand'>{product.brandName || '—'}</div>
            <div className='pmt-confidence'>
              <div className={cn('pmt-confidence-value', band)}>{getPercentDisplay(row.confidence)}</div>
              {band && (
                <div className='pmt-confidence-track'>
                  <div className={cn('pmt-confidence-fill', band)} style={{ width: getPercentDisplay(row.confidence) }} />
                </div>
              )}
            </div>
            <div className='pmt-method'>
              <FontAwesomeIcon icon={method.icon} />
              {method.display}
            </div>
            {showStatus && (
              <div>
                <span className={cn('pm-status-badge', review.className)}>{review.display}</span>
              </div>
            )}
            <div className='pmt-actions'>
              {unmatched && (
                <Tooltip message='Try again' outerClassName='pmt-action-tip'>
                  <button
                    type='button'
                    className='retry'
                    onClick={e => {
                      e.stopPropagation();
                      onRetry(row);
                    }}
                  >
                    <FontAwesomeIcon icon={faRedo} />
                  </button>
                </Tooltip>
              )}
              {grade && (
                <span className={cn('pmt-grade-badge', { model: grade.isModel })} title={grade.title}>
                  {grade.label}
                  {grade.isModel && <span className='pmt-grade-source'>model</span>}
                </span>
              )}
              {!run && Boolean(row.isExemplar) && (
                <span className='pmt-exemplar-badge' title='Strong example'>
                  <FontAwesomeIcon icon={faStarSolid} />
                </span>
              )}
              {!run && (
                <Tooltip message='Confirm' outerClassName='pmt-action-tip'>
                  <button
                    type='button'
                    className='confirm'
                    onClick={e => {
                      e.stopPropagation();
                      onConfirm(row);
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                </Tooltip>
              )}
              <Tooltip message='Edit' outerClassName='pmt-action-tip'>
                <button type='button' className='edit' onClick={() => onOpen(row)}>
                  <FontAwesomeIcon icon={faPencil} />
                </button>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductMentionsTable;
